import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDate } from '@/lib/utils/date';
import { ArrowRight, CalendarClock, CalendarDays, Database } from 'lucide-react';
import { useMemo } from 'react';
import { parseContinuousSchedule, WEEK_DAYS } from '../types';

interface ContinuousScheduleCardProps {
  startDate?: string;
  endDate?: string;
  /** Weekly schedule JSON (sparse or full — missing days = disabled). */
  scheduleJson?: string | null;
  continueWithBase?: boolean;
  baseLabels?: { on: string; off: string };
}

export function ContinuousScheduleCard({
  startDate,
  endDate,
  scheduleJson,
  continueWithBase = false,
  baseLabels = { on: 'ON', off: 'OFF' },
}: ContinuousScheduleCardProps) {
  const schedule = useMemo(() => parseContinuousSchedule(scheduleJson ?? ''), [scheduleJson]);
  const activeDays = useMemo(() => WEEK_DAYS.filter((d) => schedule[d].isEnable), [schedule]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <CalendarClock className="h-4 w-4 text-primary" />
            </span>
            <div>
              <CardTitle>Active Schedule</CardTitle>
              <CardDescription>The campaign dials only within these windows.</CardDescription>
            </div>
          </div>
          <Badge variant="outline" className="gap-1.5 font-normal">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {activeDays.length} day{activeDays.length === 1 ? '' : 's'}/week
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 px-4 py-3">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-2xs uppercase tracking-wider text-muted-foreground">From</p>
              <p className="text-sm font-medium text-foreground">
                {formatDate(startDate, 'MMM dd, yyyy') || '—'}
              </p>
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <div>
            <p className="text-2xs uppercase tracking-wider text-muted-foreground">To</p>
            <p className="text-sm font-medium text-foreground">
              {formatDate(endDate, 'MMM dd, yyyy') || '—'}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">Weekly windows</p>
          {activeDays.length > 0 ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {activeDays.map((day) => {
                const d = schedule[day];
                return (
                  <div
                    key={day}
                    className="flex items-center justify-between gap-2 rounded-lg border border-emerald-500/25 bg-emerald-500/[0.06] px-3 py-2"
                  >
                    <span className="flex items-center gap-2 text-sm font-medium capitalize text-foreground">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      {day.slice(0, 3)}
                    </span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {d.startTime} – {d.endTime}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
              No weekly windows configured yet.
            </div>
          )}
        </div>

        <div className="flex items-center justify-between rounded-lg border border-border px-4 py-2.5">
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Database className="h-4 w-4" />
            Contact base
          </span>
          <Badge
            variant="outline"
            className={
              continueWithBase
                ? 'gap-1.5 border-sky-500/25 bg-sky-500/10 font-normal text-sky-700 dark:text-sky-400'
                : 'font-normal'
            }
          >
            {continueWithBase ? baseLabels.on : baseLabels.off}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}
